import { useSearchParams } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { useGetProjectGroupByIdAdminQuery } from '@/redux/projectGroup/projectGroup.api.slice';
import Loader from '@/components/common/Loader';
import NotFound from '../NotFound';
import ShareButton from '@/components/Post/ShareButton';
import DownloadProjectGroupButton from '@/components/ProjectGroup/DownloadProjectGroupButton';
import ProjectGroupBody from '../ProjectGroupDetail/ProjectGroupBody';
import Notice from '@/components/ui/notice';
import ReviewHistorySheet from '@/components/ReviewHistory/ReviewHistorySheet';

/**
 * Admin preview of a multi-workpiece group, opened from the approvals queue
 * with a short-lived token.
 *
 * Renders `ProjectGroupBody` — the same body the public group page uses — so a
 * reviewer sees what the maker will see. The token payload has no requester, so
 * there are no like or bookmark flags to drive those controls; only Share and
 * Download are passed in.
 */
// Count is presentational gating (token guards, empty admin comments), not logic.
// fallow-ignore-next-line complexity
function ProjectGroupAdminPreview() {
  const [searchParams] = useSearchParams();
  const projectGroupId = searchParams.get('projectGroupId') || '';
  const token = searchParams.get('token') || '';

  const {
    data: projectGroup,
    isLoading,
    error,
    refetch,
  } = useGetProjectGroupByIdAdminQuery(
    { projectGroupId, token },
    { skip: !projectGroupId || !token }
  );

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const adminComments = useMemo(() => {
    return projectGroup?.adminComments || [];
  }, [projectGroup]);

  useEffect(() => {
    if (projectGroup?.projects?.length && !selectedProjectId) {
      setSelectedProjectId(projectGroup.projects[0]._id);
    }
  }, [projectGroup, selectedProjectId]);

  const currentProject = useMemo(() => {
    return projectGroup?.projects?.find((p) => p._id === selectedProjectId);
  }, [projectGroup, selectedProjectId]);

  if (isLoading) return <Loader title="Just a moment!" description="" />;

  if (error || !projectGroup || !projectGroupId) {
    return <NotFound />;
  }

  return (
    <div
      className="overflow-y-auto scroll-snap-type-y-mandatory pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      {projectGroup.visibility !== 'public' && (
        <Notice className="mx-3 mt-4 sm:mx-5">
          This Multi Workpiece is {projectGroup.visibility} and not visible to
          others.
        </Notice>
      )}

      <ProjectGroupBody
        group={projectGroup}
        currentProject={currentProject}
        selectedProjectId={selectedProjectId}
        onSelectProject={setSelectedProjectId}
        parts={{
          aggregatedModelGroups: projectGroup.aggregatedModelGroups,
          totalPartsCount: projectGroup.totalPartsCount,
        }}
        notice={
          <ReviewHistorySheet
            target={{ kind: 'projectGroup', id: projectGroupId }}
            token={token}
            comments={adminComments}
            onRefetch={refetch}
          />
        }
        interactions={
          <ShareButton
            shareUrl={`${window.location.origin}/project-groups/${projectGroupId}`}
            size="md"
          />
        }
        download={
          <DownloadProjectGroupButton
            projectGroupId={projectGroupId}
            projectGroupName={projectGroup.name}
            totalPartsCount={projectGroup.totalPartsCount}
            projects={projectGroup.projects}
          />
        }
      />
    </div>
  );
}

export default ProjectGroupAdminPreview;
